// src/hooks/useTrackTrendV2Cache.ts
import { useEffect, useState, useRef } from 'react'
import { useRouter } from 'next/router'
import { useQueryClient } from '@tanstack/react-query'

import { toast } from '@/hooks/useToast'
import { TREND_V2_TRACKING_KEY, TrendV2TrackingItem } from '@/constants/trend_v2'
import MessageQueryKeyGenerator from '@/queries/message/MessageQueryKeyGenerator'
import { Message, MessageStatus } from '@/types/message.type'
import { useAuth } from '@/hooks/useAuth'

interface UseTrackTrendV2CacheProps {
  messageId: string
  conversationId: string
  enabled: boolean
  onComplete?: () => void
}

export const useTrackTrendV2Cache = ({
  messageId,
  conversationId,
  enabled,
  onComplete,
}: UseTrackTrendV2CacheProps) => {
  const [isMonitoring, setIsMonitoring] = useState(enabled)
  const hasPolledOnceRef = useRef(false)
  const completedRef = useRef(false)
  const router = useRouter()
  const queryClient = useQueryClient()
  const { user } = useAuth()

  const getTrackingItem = () => {
    const stored = localStorage.getItem(TREND_V2_TRACKING_KEY)
    if (!stored) return null

    const items: TrendV2TrackingItem[] = JSON.parse(stored)
    return items.find((item) => item.messageId === messageId)
  }

  // Check if data is expired (24+ hours)
  const isDataExpired = () => {
    const trackingItem = getTrackingItem()
    if (!trackingItem) return false

    const startedAt = new Date(trackingItem.startedAt)
    const now = new Date()
    const hoursDiff = (now.getTime() - startedAt.getTime()) / (1000 * 60 * 60)

    return hoursDiff >= 24
  }

  // Check message status to see if processing is complete
  const checkMessageComplete = async (): Promise<boolean> => {
    try {
      // Refetch the messages to get the latest status
      await queryClient.refetchQueries({
        queryKey: MessageQueryKeyGenerator.index(user?.id, conversationId, 0),
      })

      // Query the message data from cache after refetch
      const messageData = queryClient.getQueryData<any>(
        MessageQueryKeyGenerator.index(user?.id, conversationId, 0)
      )

      if (!messageData) return false

      // Handle infinite query structure
      let messages: Message[] = []
      if (messageData.pages) {
        // Infinite query structure
        messages = messageData.pages.flatMap((page: any) => page.data || [])
      } else if (messageData.data) {
        // Regular query structure
        messages = Array.isArray(messageData.data) ? messageData.data : []
      }

      if (!messages || messages.length === 0) return false

      const message = messages.find((m) => m.id === messageId)
      if (!message) return false

      // Message is complete when it's no longer pending
      return message.status !== MessageStatus.Pending
    } catch (error) {
      console.error('Error checking TrendV2 message status:', error)
      return false
    }
  }

  const removeFromTracking = () => {
    const stored = localStorage.getItem(TREND_V2_TRACKING_KEY)
    if (!stored) return

    const items: TrendV2TrackingItem[] = JSON.parse(stored)
    const filtered = items.filter((item) => item.messageId !== messageId)

    if (filtered.length > 0) {
      localStorage.setItem(TREND_V2_TRACKING_KEY, JSON.stringify(filtered))
    } else {
      localStorage.removeItem(TREND_V2_TRACKING_KEY)
    }
  }

  // Polling effect - checks status every 10 seconds
  useEffect(() => {
    if (!isMonitoring || completedRef.current || !user) return

    const pollStatus = async () => {
      const isComplete = await checkMessageComplete()
      hasPolledOnceRef.current = true

      if (isComplete) {
        completedRef.current = true
        setIsMonitoring(false)

        const trackingItem = getTrackingItem()
        if (!trackingItem) return

        // Set completion flag for showing blue dot
        const completionKey = `trend_v2_completion_${conversationId}`
        localStorage.setItem(completionKey, 'true')

        // Dispatch custom event to notify UI components about completion
        window.dispatchEvent(
          new CustomEvent('trend_v2_completion', {
            detail: { conversationId },
          })
        )

        // Show completion toast
        toast({
          type: 'alarm',
          text: `${trackingItem.company} Reddit Consumer Report has been generated.`,
          duration: 500000,
          onClick: () => {
            router.push(`/tfs_search?conversation_id=${conversationId}`)
          },
        })

        // Remove from tracking
        removeFromTracking()

        // Call onComplete callback
        onComplete?.()
      } else if (hasPolledOnceRef.current && isDataExpired()) {
        // If polled at least once, not complete, and expired - remove from tracking
        completedRef.current = true
        setIsMonitoring(false)
        removeFromTracking()
      }
    }

    // Initial check
    pollStatus()

    // Poll every 10 seconds
    const interval = setInterval(pollStatus, 10000)

    return () => clearInterval(interval)
  }, [isMonitoring, messageId, conversationId, user?.id])

  // Enable monitoring when enabled prop changes
  useEffect(() => {
    if (enabled && !completedRef.current) {
      setIsMonitoring(true)
    }
  }, [enabled])

  return {
    isMonitoring,
    isComplete: completedRef.current,
  }
}
